import React, { useCallback, useEffect, useRef, useState } from 'react';

interface UseDragSelectionParams {
  /** Текущее состояние строки. Читается в момент жеста, поэтому обязан быть стабильным. */
  isSelected: (id: number) => boolean;
  /** Установить состояние строки. Именно установить, а не переключить: жест идемпотентен. */
  setSelected: (id: number, selected: boolean) => void;
}

interface UseDragSelection {
  /** Идёт ли жест прямо сейчас — списку на это время нужен `user-select: none`. */
  isDragging: boolean;
  handleRowMouseDown: (event: React.MouseEvent<HTMLElement>, id: number) => void;
  handleRowMouseEnter: (id: number) => void;
  handleRowClick: (id: number) => void;
}

/** Клик по кнопке или чекбоксу внутри строки — действие этого элемента, а не выделение строки. */
const isInteractive = (target: EventTarget | null) =>
  Boolean((target as HTMLElement | null)?.closest?.('button, a, input, select, [role="button"]'));

/**
 * Выделение строк списка мышью: клик по строке и протягивание по нескольким строкам.
 *
 * Живёт рядом с экраном состава команды: это ui-логика одного экрана, в `shared`
 * выносить рано. Жест устроен так:
 *
 * - `mousedown` на строке задаёт **целевое состояние** всего жеста — «ставим» или
 *   «снимаем», по текущему состоянию той строки, с которой жест начался, — и сразу
 *   применяет его к ней;
 * - `mouseenter` применяет то же состояние к строке под курсором; операция —
 *   установка, а не переключение, поэтому повторное срабатывание безвредно;
 * - `mouseup` ловится на `window`: отпустить кнопку можно и за пределами списка.
 *
 * `onClick` строки — запасной путь для случаев, когда `mousedown` не приходит.
 * После обычного клика мышью браузер шлёт `mousedown`, `mouseup` и `click` подряд,
 * поэтому клик, у которого был свой `mousedown`, пропускается: иначе строка
 * переключилась бы дважды.
 */
const useDragSelection = ({ isSelected, setSelected }: UseDragSelectionParams): UseDragSelection => {
  /** Целевое состояние жеста; `null` — жеста нет. */
  const targetRef = useRef<boolean | null>(null);
  /** Строка, на которой начался жест: её `click` уже обработан через `mousedown`. */
  const pointerIdRef = useRef<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const finish = () => {
      targetRef.current = null;
      setIsDragging(false);
      // `click` браузер шлёт сразу после `mouseup`, до макрозадач, поэтому метку
      // «этот клик уже обработан» снимаем отложенно — иначе она бы протухла и
      // проглотила следующий клик, пришедший без `mousedown`.
      window.setTimeout(() => {
        pointerIdRef.current = null;
      }, 0);
    };

    window.addEventListener('mouseup', finish);
    return () => window.removeEventListener('mouseup', finish);
  }, [isDragging]);

  const handleRowMouseDown = useCallback(
    (event: React.MouseEvent<HTMLElement>, id: number) => {
      if (isInteractive(event.target)) {
        return;
      }

      const next = !isSelected(id);
      targetRef.current = next;
      pointerIdRef.current = id;
      setIsDragging(true);
      setSelected(id, next);
    },
    [isSelected, setSelected],
  );

  const handleRowMouseEnter = useCallback(
    (id: number) => {
      if (targetRef.current === null) {
        return;
      }

      setSelected(id, targetRef.current);
    },
    [setSelected],
  );

  const handleRowClick = useCallback(
    (id: number) => {
      if (pointerIdRef.current === id) {
        pointerIdRef.current = null;
        return;
      }

      setSelected(id, !isSelected(id));
    },
    [isSelected, setSelected],
  );

  return { isDragging, handleRowMouseDown, handleRowMouseEnter, handleRowClick };
};

export default useDragSelection;
